import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getPlayers, getGPS, getScenarios, getKpiDefinitions, getTrainerRatings, saveMatchRatings, saveTrainingRatings, deleteTrainerRatings, getMatchDrills, getOpponents, getNextMatch, getScheduleWeek, getLoadScenarios, saveGPS } from '../api';
import { SCENARIO_DAYS, parseDate, getMDForDate } from '../scenarioDays';
import PlayerPicker from '../components/PlayerPicker';
import PageChrome from '../components/PageChrome';

const inp = { padding: '8px 11px', borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', fontFamily: 'inherit' };
const cellInp = { width: 56, padding: '6px 4px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', textAlign: 'center', fontFamily: 'inherit' };

const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

// ── UTC-safe date helpers (frontend has no shared twin of the backend's addDaysUTC/toDateStr
// — same reason WeekSchedule.js keeps its own small copies rather than growing scenarioDays.js
// for one page). ──
function toDateStr(d) {
  if (typeof d === 'string') return d.slice(0, 10);
  return d.toISOString().slice(0, 10);
}
function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return toDateStr(dt);
}
// The Monday of the week CONTAINING dateStr (rounds backward) — WeekSchedule.js's own
// mondayOnOrAfter rounds forward to the next upcoming Monday, which is the wrong direction
// for looking up the schedule week a past/today's match date already falls inside.
function mondayOnOrBefore(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const dow = dt.getUTCDay(); // 0=Sun..6=Sat
  const diff = dow === 0 ? -6 : -(dow - 1);
  return addDays(dateStr, diff);
}

// Same minutes-played qualifying threshold pillarScores.js's computeKpiBlend actually
// scores against (GKs exempt there too) — kept in sync so this warning never shows/hides at
// a different cutoff than what silently determines whether the rating counts (confirmed
// 2026-07-27: a rating entered here for an under-threshold appearance is saved, just never
// included in that player's tactical/identity score or shown on the Team Table).
const MIN_QUALIFYING_MINUTES = 30;

// Same total_time (mm:ss) -> minutes parser as the Match Performance report (MatchPerformance.js)
// — kept as its own copy here rather than a shared import since it's a 4-line pure function.
const parseTimeToMinutes = t => {
  if (!t) return 0;
  const p = String(t).trim().split(':').map(Number);
  if (p.length === 3) return p[0] * 60 + p[1] + p[2] / 60;
  if (p.length === 2) return p[0] + p[1] / 60;
  return 0;
};

// Temporary standalone route — this gets folded into the Tactical/Identity bubbles once
// their team-overview pages exist (Stage 6). Data capture only: no 65/35 blend, no
// 45-minutes-played fallback — that scoring logic is Stage 3 (the rollup engine).
export default function TrainerRatings({ user, onNavigate }) {
  const [context, setContext] = useState('match');
  const [players, setPlayers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [matchDrills, setMatchDrills] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [matchId, setMatchId] = useState('');
  const [scenarioId, setScenarioId] = useState('');
  const [kpis, setKpis] = useState([]);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
  const [ratings, setRatings] = useState({}); // { [playerId]: { [kpiId]: '0-10 string' } }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [opponents, setOpponents] = useState([]);
  const [customScenarioDays, setCustomScenarioDays] = useState({});
  // A scenario-scheduled match day (today or the couple of days before it) with no matching
  // gps_sessions row yet — see the detection effect below. null once resolved/handled or when
  // there isn't one.
  const [pendingMatch, setPendingMatch] = useState(null);
  const [pendingOpponentPick, setPendingOpponentPick] = useState('');
  const [creatingStub, setCreatingStub] = useState(false);
  const [stubError, setStubError] = useState('');

  useEffect(() => {
    getPlayers().then(p => setPlayers((p || []).filter(pl => pl.active !== false))).catch(() => {});
    getGPS().then(sessions => {
      const list = (sessions || []).filter(s => s.session_type === 'match')
        .sort((a, b) => new Date(b.session_date) - new Date(a.session_date));
      setMatches(list);
    }).catch(() => {});
    getScenarios().then(rows => {
      const list = (rows || []).filter(s => s.player_id === 'team')
        .sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
      setScenarios(list);
    }).catch(() => {});
    getKpiDefinitions().then(rows => setKpis((rows || []).filter(k => k.pillar === 'tactical' || k.pillar === 'identity'))).catch(() => {});
    getMatchDrills().then(rows => setMatchDrills(rows || [])).catch(() => {});
    getOpponents().then(rows => setOpponents(rows || [])).catch(() => {});
    getLoadScenarios().then(rows => {
      const map = {};
      (rows || []).forEach(t => { map[t.name] = t.days; });
      setCustomScenarioDays(map);
    }).catch(() => {});
  }, []);

  // Detects a match day (per the active team scenario, same MD-label logic Upload Zone uses)
  // within the last 3 calendar days that has no gps_sessions row yet — a trainer wanting to
  // rate today's match before GPS has been uploaded. Resolves the opponent from Week Schedule
  // first (the trainer-confirmed value for that date), falling back to the Next Match widget
  // if it happens to point at the same date; if neither resolves, the banner below asks the
  // trainer to pick the opponent manually rather than guessing — the opponent name becomes the
  // stub session's title, and it must land exactly on whatever the real GPS CSV's own "Session
  // Title" column will be later (server.production.js's /api/gps dedup is an exact string
  // match, not case-insensitive), so a confirmed value beats a guess here.
  useEffect(() => {
    if (context !== 'match' || !scenarios.length) { setPendingMatch(null); return; }
    const today = toDateStr(new Date());
    const existingDates = new Set(matches.map(m => toDateStr(m.session_date)));
    const candidates = [today, addDays(today, -1), addDays(today, -2)];
    const allScenarioDays = { ...SCENARIO_DAYS, ...customScenarioDays };
    let found = null;
    for (const dateStr of candidates) {
      if (existingDates.has(dateStr)) continue;
      const sc = scenarios.find(s => parseDate(dateStr) >= parseDate(s.start_date));
      if (!sc) continue;
      if (getMDForDate(sc, dateStr, allScenarioDays) === 'MD') { found = { date: dateStr, scenario: sc.scenario }; break; }
    }
    if (!found) { setPendingMatch(null); return; }

    let cancelled = false;
    (async () => {
      let opponentName = null;
      try {
        const week = await getScheduleWeek(mondayOnOrBefore(found.date));
        const day = (week?.days || []).find(d => toDateStr(d.schedule_date) === found.date);
        if (day?.opponent_name) opponentName = day.opponent_name;
      } catch {}
      if (!opponentName) {
        try {
          const nm = await getNextMatch();
          if (nm && toDateStr(nm.match_date) === found.date) opponentName = nm.opponent;
        } catch {}
      }
      const matched = opponentName ? opponents.find(o => o.name.toLowerCase() === opponentName.toLowerCase()) : null;
      if (cancelled) return;
      setPendingMatch({ date: found.date, scenario: found.scenario, opponentName: matched?.name || null, opponentId: matched?.id || null });
      setPendingOpponentPick('');
      setStubError('');
    })();
    return () => { cancelled = true; };
  }, [context, matches, scenarios, opponents, customScenarioDays]);

  // Creates the gps_sessions stub (zero player rows, skip_notification so the squad isn't
  // paged for a session with no actual GPS data) so a real match_id exists to rate against —
  // reuses POST /api/gps verbatim rather than a parallel endpoint, which is also what makes
  // the later real CSV upload find-and-reuse this same row instead of duplicating it (same
  // date+title dedup /api/gps already does for every other upload).
  const handleStartPendingMatch = async () => {
    if (!pendingMatch) return;
    const opponentId = pendingMatch.opponentId || (pendingOpponentPick ? Number(pendingOpponentPick) : null);
    const opponent = opponents.find(o => o.id === opponentId);
    if (!opponent) { setStubError('Pick the opponent before starting.'); return; }
    setCreatingStub(true);
    setStubError('');
    try {
      const result = await saveGPS({
        date: pendingMatch.date,
        title: opponent.name,
        scenario: pendingMatch.scenario,
        sessionType: 'match',
        opponentId: opponent.id,
        skip_notification: true,
        players: [],
      });
      const sessionId = result?.sessionId;
      if (!sessionId) throw new Error('No session returned.');
      setMatches(prev => [
        { id: sessionId, session_date: pendingMatch.date, session_title: opponent.name, opponent_name: opponent.name, session_type: 'match' },
        ...prev,
      ]);
      setMatchId(String(sessionId));
      setPendingMatch(null);
    } catch (err) {
      setStubError(err.message || 'Failed to create match.');
    } finally {
      setCreatingStub(false);
    }
  };

  const tacticalKpis = useMemo(() => kpis.filter(k => k.pillar === 'tactical'), [kpis]);
  const identityKpis = useMemo(() => kpis.filter(k => k.pillar === 'identity'), [kpis]);

  const contextKey = context === 'match' ? matchId : scenarioId;

  // Minutes-played lookup for the selected match only (2026-07-23), sourced the same way as
  // the Match Performance report (MatchPerformance.js): summed per-player from the match's
  // half-drill total_time entries, not gps_data.minutes_played — that column is unreliable/
  // often NULL (e.g. for GKs, per the trainer-ratings GK fix), while the drill-level total_time
  // is the real per-match minutes source of truth the rest of the app already reports against.
  // Drills key by player_name (no player_id on gps_drills), matched case-insensitively.
  // Training scenarios have no equivalent minutes concept, so this stays empty outside match.
  const minutesByPlayer = useMemo(() => {
    if (context !== 'match' || !matchId) return {};
    const drills = matchDrills.filter(d => String(d.session_id) === String(matchId));
    const byName = {};
    drills.forEach(d => {
      const key = (d.player_name || '').toLowerCase();
      byName[key] = (byName[key] || 0) + parseTimeToMinutes(d.total_time);
    });
    const map = {};
    players.forEach(p => {
      const mins = byName[(p.name || '').toLowerCase()];
      if (mins != null) map[p.id] = mins;
    });
    return map;
  }, [context, matchId, matchDrills, players]);

  // Reopening a match/scenario that already has ratings pre-fills the grid for editing.
  const loadExisting = useCallback(() => {
    if (!contextKey) { setRatings({}); setSelectedPlayerIds([]); return; }
    setLoading(true);
    const params = context === 'match' ? { context, match_id: matchId } : { context, scenario_id: scenarioId };
    getTrainerRatings(params).then(rows => {
      const nextRatings = {};
      const playerIds = [];
      (rows || []).forEach(r => {
        if (!nextRatings[r.player_id]) { nextRatings[r.player_id] = {}; playerIds.push(r.player_id); }
        nextRatings[r.player_id][r.kpi_id] = r.rating != null ? String(r.rating) : '';
      });
      setRatings(nextRatings);
      setSelectedPlayerIds(playerIds);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [context, matchId, scenarioId, contextKey]);

  useEffect(() => { loadExisting(); }, [loadExisting]);

  const handleContextChange = (next) => {
    setContext(next);
    setMatchId('');
    setScenarioId('');
    setRatings({});
    setSelectedPlayerIds([]);
    setSaved(false);
    setError('');
  };

  const setCell = (playerId, kpiId, value) => {
    setSaved(false);
    setRatings(prev => ({ ...prev, [playerId]: { ...prev[playerId], [kpiId]: value } }));
  };

  const handleSave = async () => {
    setError('');
    setSaved(false);
    if (!contextKey) { setError(context === 'match' ? 'Select a match first.' : 'Select a scenario first.'); return; }
    const entries = [];
    selectedPlayerIds.forEach(playerId => {
      kpis.forEach(k => {
        const v = ratings[playerId]?.[k.id];
        if (v === undefined || v === null || v === '') return;
        entries.push({ player_id: playerId, kpi_id: k.id, rating: Number(v) });
      });
    });
    if (!entries.length) { setError('Enter at least one rating before saving.'); return; }
    setSaving(true);
    try {
      if (context === 'match') {
        await saveMatchRatings({ match_id: matchId, rated_by_user_id: user.id, ratings: entries });
      } else {
        await saveTrainingRatings({ scenario_id: scenarioId, rated_by_user_id: user.id, ratings: entries });
      }
      setSaved(true);
    } catch (err) {
      setError(err.message || 'Failed to save ratings.');
    } finally {
      setSaving(false);
    }
  };

  // Only meaningful once ratings have actually been loaded for this match/scenario — an
  // empty grid the trainer just opened but hasn't saved yet has nothing to delete.
  const hasExistingRatings = selectedPlayerIds.length > 0 && Object.keys(ratings).length > 0;

  const handleDelete = async () => {
    if (!contextKey) return;
    const label = context === 'match' ? 'this match' : 'this scenario';
    if (!window.confirm(`Delete all Tactical/Identity ratings for ${label}? This can't be undone.`)) return;
    setError('');
    setSaving(true);
    try {
      const params = context === 'match' ? { context, match_id: matchId } : { context, scenario_id: scenarioId };
      await deleteTrainerRatings(params);
      setRatings({});
      setSelectedPlayerIds([]);
      setSaved(false);
    } catch (err) {
      setError(err.message || 'Failed to delete ratings.');
    } finally {
      setSaving(false);
    }
  };

  const selectedPlayers = selectedPlayerIds
    .map(id => players.find(p => p.id === id))
    .filter(Boolean)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  return (
    <PageChrome onNavigate={onNavigate} user={user}>
    <div style={{ maxWidth: 960 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 4px' }}>Trainer Ratings — Tactical &amp; Identity</h2>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 20px' }}>
        Enter 0-10 ratings per player per KPI. Match ratings are one per player; training ratings are one blended entry per scenario, not per session.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[['match', 'Match'], ['training', 'Training']].map(([val, label]) => (
          <button key={val} onClick={() => handleContextChange(val)}
            style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${context === val ? '#2563eb' : 'var(--border-strong)'}`, background: context === val ? '#2563eb' : 'transparent', color: context === val ? '#fff' : 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            {label}
          </button>
        ))}
      </div>

      {context === 'match' && pendingMatch && (
        <div style={{ width: '100%', padding: '10px 14px', marginBottom: 12, borderRadius: 8, border: '1px solid #f59e0b', background: 'rgba(245,158,11,0.08)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 13 }}>
          <span style={{ color: '#f59e0b', fontWeight: 700 }}>⚠</span>
          <span style={{ color: 'var(--text-secondary)' }}>
            {fmtDate(pendingMatch.date)} match{pendingMatch.opponentName ? ` vs ${pendingMatch.opponentName}` : ''} — GPS not uploaded yet.
          </span>
          {!pendingMatch.opponentName && (
            <select value={pendingOpponentPick} onChange={e => setPendingOpponentPick(e.target.value)} style={{ ...inp, padding: '5px 8px', fontSize: 12 }}>
              <option value="">Who did we play?</option>
              {opponents.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}
          <button
            disabled={creatingStub || (!pendingMatch.opponentName && !pendingOpponentPick)}
            onClick={handleStartPendingMatch}
            style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#f59e0b', color: '#000', fontWeight: 700, fontSize: 12, cursor: creatingStub ? 'default' : 'pointer' }}>
            {creatingStub ? 'Starting…' : 'Start rating now'}
          </button>
          {stubError && <span style={{ color: '#ef4444', fontSize: 12 }}>{stubError}</span>}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        {context === 'match' ? (
          <select value={matchId} onChange={e => { setMatchId(e.target.value); setSaved(false); }} style={{ ...inp, minWidth: 260 }}>
            <option value="">Select a match…</option>
            {matches.map(m => (
              <option key={m.id} value={m.id}>
                {fmtDate(m.session_date)}{m.opponent_name ? ` — vs ${m.opponent_name}` : m.session_title ? ` — ${m.session_title}` : ''}
              </option>
            ))}
          </select>
        ) : (
          <select value={scenarioId} onChange={e => { setScenarioId(e.target.value); setSaved(false); }} style={{ ...inp, minWidth: 260 }}>
            <option value="">Select a scenario…</option>
            {scenarios.map(s => (
              <option key={s.id} value={s.id}>{s.scenario} — {fmtDate(s.start_date)}</option>
            ))}
          </select>
        )}

        <div style={{ minWidth: 260, flex: 1 }}>
          <PlayerPicker players={players} selectedIds={selectedPlayerIds} onChange={setSelectedPlayerIds} />
        </div>
      </div>

      {loading && <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</p>}

      {!loading && contextKey && selectedPlayers.length > 0 && (
        <div style={{ overflowX: 'auto', border: '1px solid var(--border-strong)', borderRadius: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-app)' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }} rowSpan={2}>Player</th>
                <th style={{ padding: '6px 16px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#3b82f6', textTransform: 'uppercase', letterSpacing: '0.05em', borderLeft: '1px solid var(--border-strong)' }} colSpan={tacticalKpis.length}>Tactical</th>
                <th style={{ padding: '6px 16px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#8b5cf6', textTransform: 'uppercase', letterSpacing: '0.05em', borderLeft: '1px solid var(--border-strong)' }} colSpan={identityKpis.length}>Identity</th>
                {context === 'match' && (
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', borderLeft: '1px solid var(--border-strong)' }} rowSpan={2}>Minutes played</th>
                )}
              </tr>
              <tr style={{ background: 'var(--bg-app)' }}>
                {tacticalKpis.map((k, i) => (
                  <th key={k.id} style={{ padding: '6px 10px', textAlign: 'center', fontSize: 11, fontWeight: 500, color: 'var(--text-dimmed)', whiteSpace: 'nowrap', borderLeft: i === 0 ? '1px solid var(--border-strong)' : 'none' }}>{k.name}</th>
                ))}
                {identityKpis.map((k, i) => (
                  <th key={k.id} style={{ padding: '6px 10px', textAlign: 'center', fontSize: 11, fontWeight: 500, color: 'var(--text-dimmed)', whiteSpace: 'nowrap', borderLeft: i === 0 ? '1px solid var(--border-strong)' : 'none' }}>{k.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {selectedPlayers.map(p => (
                <tr key={p.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{p.name}</td>
                  {tacticalKpis.map((k, i) => (
                    <td key={k.id} style={{ padding: '6px 10px', textAlign: 'center', borderLeft: i === 0 ? '1px solid var(--border-strong)' : 'none' }}>
                      <input type="number" min="0" max="10" step="0.5" value={ratings[p.id]?.[k.id] ?? ''}
                        onChange={e => setCell(p.id, k.id, e.target.value)} style={cellInp} />
                    </td>
                  ))}
                  {identityKpis.map((k, i) => (
                    <td key={k.id} style={{ padding: '6px 10px', textAlign: 'center', borderLeft: i === 0 ? '1px solid var(--border-strong)' : 'none' }}>
                      <input type="number" min="0" max="10" step="0.5" value={ratings[p.id]?.[k.id] ?? ''}
                        onChange={e => setCell(p.id, k.id, e.target.value)} style={cellInp} />
                    </td>
                  ))}
                  {context === 'match' && (() => {
                    const mins = minutesByPlayer[p.id];
                    const isGK = p.position === 'GK';
                    const underThreshold = !isGK && (mins == null || mins < MIN_QUALIFYING_MINUTES);
                    return (
                      <td style={{ padding: '6px 10px', textAlign: 'center', fontSize: 13, borderLeft: '1px solid var(--border-strong)' }}>
                        <span style={{ color: underThreshold ? '#f59e0b' : 'var(--text-secondary)', fontWeight: underThreshold ? 700 : 400 }}>
                          {mins != null ? Math.round(mins) : '—'}
                        </span>
                        {underThreshold && (
                          <div style={{ fontSize: 10, color: '#f59e0b', marginTop: 2, whiteSpace: 'nowrap' }}
                            title={`Under ${MIN_QUALIFYING_MINUTES} minutes played — this rating is saved but won't count toward this player's tactical/identity score or show on the Team Table.`}>
                            ⚠ won't count
                          </div>
                        )}
                      </td>
                    );
                  })()}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && contextKey && selectedPlayers.length === 0 && (
        <p style={{ fontSize: 13, color: 'var(--text-dimmed)' }}>Select players above to start entering ratings.</p>
      )}

      {error && <p style={{ color: '#ef4444', fontSize: 13, marginTop: 12 }}>{error}</p>}
      {saved && !error && <p style={{ color: '#22c55e', fontSize: 13, marginTop: 12 }}>Ratings saved.</p>}

      <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
        <button disabled={saving || !contextKey} onClick={handleSave}
          style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: saving || !contextKey ? 'var(--border-strong)' : '#2563eb', color: '#fff', cursor: saving || !contextKey ? 'default' : 'pointer', fontSize: 13, fontWeight: 600 }}>
          {saving ? 'Saving…' : 'Save ratings'}
        </button>
        {hasExistingRatings && (
          <button disabled={saving} onClick={handleDelete}
            style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid #ef4444', background: 'transparent', color: '#ef4444', cursor: saving ? 'default' : 'pointer', fontSize: 13, fontWeight: 600 }}>
            Delete ratings for this {context === 'match' ? 'match' : 'scenario'}
          </button>
        )}
      </div>
    </div>
    </PageChrome>
  );
}
